import { useNavigate } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useCallback, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"

import type { InvoicesSortableAccessorKey } from "../constants/sorting"
import { accessorKeyToSortField, sortFieldToAccessorKey } from "../constants/sorting"
import type { InvoicesSearchParams } from "../schemas"
import { useInvoicesSearchStore } from "../stores/useInvoicesSearchStore"

import { useAs400SyncAutoRefresh } from "./useAs400SyncAutoRefresh"

import { type Invoice, InvoiceSortField, SortingOrder } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseInvoiceListReturn {
  invoices: Invoice[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  searchParams: InvoicesSearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
  handleFilterChange: <K extends keyof InvoicesSearchParams>(key: K, value: InvoicesSearchParams[K]) => void
  handleFilterRemove: (key: keyof InvoicesSearchParams, value?: string | number) => void
  handleFilterClear: () => void
}

const ROUTE = "/app/accounting/_layout/accounts-payable"

const FILTER_KEYS: (keyof InvoicesSearchParams)[] = ["statuses", "invoiceType", "vendorCompanyId"]

export const useInvoiceList = (): UseInvoiceListReturn => {
  const store = useInvoicesSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, refreshLoaded, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      refreshLoaded,
      clearStore,
    }))
  )

  const {
    data: invoices,
    isFetching,
    error,
    searchParams,
    tableContainerRef,
    refetch,
    refresh,
    handleScroll,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useListPageBase<Invoice, InvoicesSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  useAs400SyncAutoRefresh(invoices, refresh)

  // Sorting uses an explicit accessorKey↔BE-enum map because the column accessor
  // `invoiceNumber` does not camelCase-roundtrip to `VENDOR_INVOICE_NUMBER`.
  const { field, order } = searchParams
  const sorting = useMemo<SortingState>(() => {
    if (!field) return []
    const accessorKey = sortFieldToAccessorKey.get(field)
    if (!accessorKey) return []
    return [{ id: accessorKey, desc: order === SortingOrder.Desc }]
  }, [field, order])

  const navigate = useNavigate({ from: "/app/accounting/accounts-payable" })

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater
      if (!newSorting.length) return
      const sort = newSorting[0]
      const sortField =
        accessorKeyToSortField.get(sort.id as InvoicesSortableAccessorKey) ?? InvoiceSortField.DateEntered

      navigate({
        search: (prev) => ({
          ...prev,
          field: sortField,
          order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        }),
        to: "/app/accounting/accounts-payable",
        replace: true,
      })
    },
    [navigate, sorting]
  )

  return {
    invoices,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
